/**
 * Disposable / temporary email domain blocklist.
 * Used to prevent throwaway accounts that abuse free trials and skew analytics.
 *
 * Sourced from common public lists (mailinator, guerrillamail, tempmail, etc.).
 * Keep this list focused on the most-used services — false positives hurt real users.
 */
const DISPOSABLE_EMAIL_DOMAINS = new Set<string>([
  // Mailinator family
  'mailinator.com',
  'mailinator.net',
  'mailinator2.com',
  'mailinater.com',
  'binkmail.com',
  'bobmail.info',
  'chammy.info',
  'devnullmail.com',
  'letthemeatspam.com',
  'mailin8r.com',
  'mailnesia.com',
  'reallymymail.com',
  'safetymail.info',
  'sogetthis.com',
  'spamherelots.com',
  'suremail.info',
  'thisisnotmyrealemail.com',
  'tradermail.info',
  'veryrealemail.com',
  'zippymail.info',

  // Guerrilla Mail
  'guerrillamail.com',
  'guerrillamail.net',
  'guerrillamail.org',
  'guerrillamail.biz',
  'guerrillamail.de',
  'guerrillamailblock.com',
  'sharklasers.com',
  'grr.la',
  'spam4.me',
  'pokemail.net',

  // 10MinuteMail
  '10minutemail.com',
  '10minutemail.net',
  '10minutemail.org',
  '10minutemail.co.uk',
  '10minutemail.de',
  '20minutemail.com',
  '30minutemail.com',

  // TempMail family
  'temp-mail.org',
  'temp-mail.io',
  'temp-mail.ru',
  'tempmail.com',
  'tempmail.net',
  'tempmail.org',
  'tempmailo.com',
  'tempmail.dev',
  'tempmailaddress.com',
  'tempinbox.com',
  'tempinbox.co.uk',
  'tempr.email',
  'temporary-mail.net',
  'temporarymail.com',

  // Throwaway / yopmail
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'cool.fr.nf',
  'jetable.fr.nf',
  'nospam.ze.tc',
  'nomail.xl.cx',
  'mega.zik.dj',
  'speed.1s.fr',
  'courriel.fr.nf',
  'moncourrier.fr.nf',
  'monemail.fr.nf',
  'monmail.fr.nf',

  // Throwawaymail / fake / dropmail
  'throwawaymail.com',
  'throwam.com',
  'fakeinbox.com',
  'fakemail.net',
  'fakemailgenerator.com',
  'getairmail.com',
  'dropmail.me',
  'dispostable.com',
  'discard.email',
  'discardmail.com',
  'discardmail.de',

  // Generic / often-abused
  'mailcatch.com',
  'mailnull.com',
  'maildrop.cc',
  'mailmoat.com',
  'mailsac.com',
  'mailtrash.net',
  'mailmetrash.com',
  'mintemail.com',
  'mt2014.com',
  'mt2015.com',
  'mytemp.email',
  'mytrashmail.com',
  'no-spam.ws',
  'noclickemail.com',
  'nomail.pw',
  'nowmymail.com',
  'objectmail.com',
  'obobbo.com',
  'one-time.email',
  'pjjkp.com',
  'sneakemail.com',
  'sofort-mail.de',
  'spambog.com',
  'spambox.us',
  'spamfree24.org',
  'spamgourmet.com',
  'spamhole.com',
  'spaml.com',
  'spamspot.com',
  'tmail.ws',
  'trashmail.com',
  'trashmail.net',
  'trashmail.org',
  'trashmail.de',
  'trashmail.me',
  'trashmail.ws',
  'trbvm.com',
  'wegwerfmail.de',
  'wegwerfmail.net',
  'wegwerfmail.org',
  'wegwerpmailadres.nl',
  'whyspam.me',
  'inboxbear.com',
  'inboxalias.com',
  'inboxkitten.com',
  'emailondeck.com',
  'emailfake.com',
  'emaildrop.io',
  'getnada.com',
  'nada.email',
  'mohmal.com',
  'kasmail.com',
  'altmails.com',
  'snapmail.cc',
  'burnermail.io',
  'anonbox.net',
  'anonymbox.com',
  'spambox.xyz',
  'rainmail.biz',
  'fivemail.de',
  'hidemail.de',
  'klzlk.com',
  'kurzepost.de',
  'mailblocks.com',
  'mailexpire.com',
  'mailfreeonline.com',
  'mailimate.com',
  'mailnator.com',
  'mailshell.com',
  'mailtemp.info',
  'mailtothis.com',
  'meltmail.com',
  'mintemail.cf',
  'msgwire.com',
  'plexolan.de',
  'rcpt.at',
  'recode.me',
  'recursor.net',
  'regbypass.com',
  'safetymail.com',
  'sandelf.de',
  'selfdestructingmail.com',
  'shitmail.me',
  'shortmail.net',
  'sneakmail.de',
  'spamavert.com',
  'spaminator.de',
  'spamoff.de',
  'spamthisplease.com',
  'tempemail.net',
  'tempomail.fr',
  'tempymail.com',
  'thrma.com',
  'tmpmail.net',
  'tmpmail.org',
  'tmpemail.com',
  'tmpeml.info',
  'tyldd.com',
  'wuzup.net',
  'mvrht.com',
  'nincsmail.hu',
  'qq.com.cn',
]);

/**
 * Returns true if the given email's domain is on the disposable blocklist.
 * Comparison is case-insensitive and handles "+tag" subaddresses.
 */
export const isDisposableEmail = (email: string): boolean => {
  if (!email) return false;
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  if (at === -1) return false;
  const domain = trimmed.slice(at + 1);
  if (!domain) return false;
  return DISPOSABLE_EMAIL_DOMAINS.has(domain);
};

export const DISPOSABLE_EMAIL_ERROR =
  'Please use a permanent email address. Temporary or disposable email services are not allowed.';
